// Dynamic Diesels — Influencer page behaviour.
//
// Self-contained on purpose. The homepage feed lives in dd-youtube.js, which
// is NOT on the live theme; duplicating the logic here under its own selectors
// ([data-inf-yt-*]) and its own cache key means this page works without
// pushing dd-youtube.js and changing how the homepage behaves.
//
// Difference from the homepage: there, the Shorts row is manually curated.
// Here BOTH rows auto-pull. One pass over the channel's recent uploads is
// split by duration into long-form and Shorts, so a partner page stays current
// without anyone maintaining it.
//
// Tiles are built with the dd-yt__* class names so the already-live
// dd-youtube.css styles them, and dd-yt-modal.js picks them up via
// [data-yt-embed].

(function () {
  'use strict';

  var TTL = 6 * 60 * 60 * 1000;  // 6 hours, same quota budget as the homepage
  var POOL = 50;                 // playlistItems max per call; 1 quota unit either way
  var SHORT_MAX_SECONDS = 180;   // Shorts cap at 180s; real builds/reviews run far longer

  /* ------------------------------------------------------------------ YouTube */

  function initFeed() {
    var grid = document.querySelector('[data-inf-yt-grid]');
    if (!grid) return;

    var apiKey = (grid.getAttribute('data-inf-yt-key') || '').trim();
    var handle = (grid.getAttribute('data-inf-yt-handle') || '').replace(/^@/, '').trim();
    if (!apiKey || !handle) { hideSection(grid); return; }

    var count = parseInt(grid.getAttribute('data-inf-yt-count'), 10) || 4;

    var shortsGrid = document.querySelector('[data-inf-yt-shorts]');
    var shortsCount = shortsGrid
      ? (parseInt(shortsGrid.getAttribute('data-inf-yt-shorts-count'), 10) || 6)
      : 0;

    var cached = readCache(handle);
    if (cached) {
      // Deferred to the next tick on purpose. A synchronous paint here would
      // dispatch shopify:section:load before every other deferred script had
      // registered its listener, so the lightbox would miss the new tiles and
      // nothing would be clickable on a cached visit.
      setTimeout(function () {
        paint(grid, shortsGrid, cached, count, shortsCount);
      }, 0);
      return;
    }

    load(apiKey, handle)
      .then(function (data) {
        if (!data || (!data.videos.length && !data.shorts.length)) { hideSection(grid); return; }
        writeCache(handle, data);
        paint(grid, shortsGrid, data, count, shortsCount);
      })
      .catch(function () { hideSection(grid); });
  }

  function paint(grid, shortsGrid, data, count, shortsCount) {
    var anyVideos = data.videos.length > 0;
    var anyShorts = shortsGrid && data.shorts.length > 0;

    if (anyVideos) {
      render(grid, data.videos.slice(0, count), false);
    } else {
      grid.hidden = true;
    }

    if (anyShorts) {
      render(shortsGrid, data.shorts.slice(0, shortsCount), true);
      var wrap = shortsGrid.closest('[data-inf-yt-shorts-wrap]');
      if (wrap) wrap.hidden = false;
    }

    // Nothing at all came back: drop the band rather than leave a bare heading.
    if (!anyVideos && !anyShorts) hideSection(grid);
  }

  function hideSection(el) {
    var sec = el.closest('[data-inf-yt-section]');
    if (sec) sec.hidden = true;
  }

  function load(apiKey, handle) {
    var base = 'https://www.googleapis.com/youtube/v3/';

    return fetch(base + 'channels?part=contentDetails&forHandle=' + encodeURIComponent(handle) + '&key=' + apiKey)
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var uploads = d && d.items && d.items[0] &&
          d.items[0].contentDetails.relatedPlaylists.uploads;
        if (!uploads) throw new Error('no uploads playlist');
        return fetch(base + 'playlistItems?part=snippet&maxResults=' + POOL +
          '&playlistId=' + uploads + '&key=' + apiKey);
      })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var items = (d.items || []).map(function (it) {
          var s = it.snippet || {};
          var id = s.resourceId && s.resourceId.videoId;
          var t = s.thumbnails || {};
          var thumb = (t.maxres || t.standard || t.high || t.medium || t.default || {}).url ||
            ('https://img.youtube.com/vi/' + id + '/hqdefault.jpg');
          return { id: id, title: s.title || '', thumb: thumb };
        }).filter(function (v) { return v.id; });

        if (!items.length) return { videos: [], shorts: [] };

        var ids = items.map(function (v) { return v.id; }).join(',');
        return fetch(base + 'videos?part=contentDetails&id=' + ids + '&key=' + apiKey)
          .then(function (r) { return r.json(); })
          .then(function (dd) {
            var dur = {};
            (dd.items || []).forEach(function (v) {
              dur[v.id] = isoToSeconds(v.contentDetails.duration);
            });
            return split(items, dur);
          })
          // Durations unavailable: fall back to title hints so the long-form
          // row still fills rather than showing nothing.
          .catch(function () { return split(items, null); });
      });
  }

  function split(items, dur) {
    var videos = [];
    var shorts = [];
    items.forEach(function (v) {
      var hinted = /#shorts/i.test(v.title);
      var byDuration = dur && dur[v.id] && dur[v.id] <= SHORT_MAX_SECONDS;
      if (hinted || byDuration) shorts.push(v);
      else videos.push(v);
    });
    return { videos: videos, shorts: shorts };
  }

  // ISO 8601 duration (PT1M30S) → seconds
  function isoToSeconds(iso) {
    var m = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/.exec(iso || '');
    if (!m) return 0;
    return (parseInt(m[1] || 0, 10) * 3600) + (parseInt(m[2] || 0, 10) * 60) + parseInt(m[3] || 0, 10);
  }

  function render(grid, list, isShort) {
    if (!list.length) return;
    grid.innerHTML = '';

    list.forEach(function (v) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = isShort ? 'dd-yt__tile dd-yt__tile--short' : 'dd-yt__tile';
      btn.setAttribute('data-yt-embed', 'https://www.youtube-nocookie.com/embed/' + v.id + '?autoplay=1&rel=0');
      btn.setAttribute('aria-label', 'Play video: ' + v.title);

      var thumbWrap = document.createElement('span');
      thumbWrap.className = 'dd-yt__thumb';

      var img = document.createElement('img');
      img.src = v.thumb;
      img.alt = '';
      img.loading = 'lazy';
      img.width = isShort ? 270 : 480;
      img.height = isShort ? 480 : 360;

      var play = document.createElement('span');
      play.className = 'dd-yt__play';
      play.setAttribute('aria-hidden', 'true');
      play.innerHTML = '<iconify-icon icon="material-symbols:play-arrow-rounded"></iconify-icon>';

      thumbWrap.appendChild(img);
      thumbWrap.appendChild(play);

      var cap = document.createElement('span');
      cap.className = 'dd-yt__caption';
      cap.textContent = v.title; // textContent keeps odd titles safe

      btn.appendChild(thumbWrap);
      btn.appendChild(cap);
      grid.appendChild(btn);
    });

    // Re-bind the shared lightbox to the tiles we just injected.
    document.dispatchEvent(new CustomEvent('shopify:section:load'));
  }

  // v2: cache shape changed from an array to { videos, shorts }.
  function cacheKey(handle) { return 'dd_inf_yt_v2_' + handle; }

  function readCache(handle) {
    try {
      var raw = localStorage.getItem(cacheKey(handle));
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || (Date.now() - obj.ts) > TTL) return null;
      if (!obj.data || !obj.data.videos) return null;
      return obj.data;
    } catch (e) { return null; }
  }

  function writeCache(handle, data) {
    try {
      localStorage.setItem(cacheKey(handle), JSON.stringify({ ts: Date.now(), data: data }));
    } catch (e) {}
  }

  /* -------------------------------------------------------- Discount code copy */

  function initCopy() {
    var host = document.querySelector('[data-inf-code]');
    if (!host) return;
    var btn = host.querySelector('[data-inf-copy]');
    if (!btn || !navigator.clipboard) return;

    var label = btn.querySelector('[data-inf-copy-label]');

    btn.addEventListener('click', function () {
      navigator.clipboard.writeText(host.getAttribute('data-inf-code') || '').then(function () {
        host.classList.add('is-copied');
        if (label) label.textContent = 'Copied';
        setTimeout(function () {
          host.classList.remove('is-copied');
          if (label) label.textContent = 'Copy';
        }, 1800);
      });
    });
  }

  function init() {
    initFeed();
    initCopy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
